/**
 * React Query hooks for scenario management
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import * as api from './api';
import type { NarrationDialogueSwapConfirmation, NarrationDialogueSwapOptions, UpdateScenarioPayload, UpdateScenePayload } from './types';

/**
 * Hook to fetch all scenarios
 */
export function useScenarios() {
  return useQuery({
    queryKey: ['scenarios'],
    queryFn: () => api.listScenarios(),
  });
}

/**
 * Hook to fetch a specific scenario
 */
export function useScenario(scenarioId: string | null) {
  return useQuery({
    queryKey: ['scenarios', scenarioId],
    queryFn: () => api.getScenario(scenarioId!),
    enabled: !!scenarioId,
  });
}

/**
 * Hook to update a scenario
 */
export function useUpdateScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scenarioId, payload }: { scenarioId: string; payload: UpdateScenarioPayload }) =>
      api.updateScenario(scenarioId, payload),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
      queryClient.setQueryData(['scenarios', data.id], data);
    },
  });
}

/**
 * Hook to duplicate a scenario
 */
export function useDuplicateScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scenarioId: string) => api.duplicateScenario(scenarioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * セリフとナレーションの入れ替えの実行 (docs/features/scenario-narration-dialogue-swap-plan.md §3.9手順6)。
 * dry_runはプレビューの状態機械 (useNarrationDialogueSwapPreview) がapiを直接呼ぶ。
 * 成功時は当該シナリオのクエリと声の対応表 (voice-map) をinvalidateする。モーションの同期の鮮度
 * (useMotionSyncStatus) はreact-queryではなくlastResultの要約 (motionSyncSignature) の変化で取り直す。
 * 要約には台本の契約によらずナレーション本文・セリフ行・声の素材を含めてあるので (素材IDと尺が同じまま本文だけ
 * 変わる入れ替えでも取り直すため)、呼び出し側が応答をlastResultにマージすればよい。
 */
export function useSwapNarrationDialogue() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      scenarioId,
      options,
      confirmation,
    }: {
      scenarioId: string;
      options: NarrationDialogueSwapOptions;
      confirmation: NarrationDialogueSwapConfirmation;
    }) => api.swapNarrationDialogue(scenarioId, { ...options, dry_run: false, confirmation }),
    onSuccess: (_data, { scenarioId }) => {
      queryClient.invalidateQueries({ queryKey: ['scenarios', scenarioId] });
      queryClient.invalidateQueries({ queryKey: api.scenarioVoiceMapQueryKey(scenarioId) });
    },
  });
}

/**
 * Hook to update a scene
 */
export function useUpdateScene() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ sceneId, payload }: { sceneId: string; payload: UpdateScenePayload }) =>
      api.updateScene(sceneId, payload),
    onSuccess: () => {
      // Invalidate the parent scenario to refetch with updated scene
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * Hook to create a scene
 */
export function useCreateScene() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scenarioId, payload }: { scenarioId: string; payload: { setting?: string; visual?: string; script?: string; narration?: string; dialogue?: string; after_scene_id?: string } }) =>
      api.createScene(scenarioId, payload),
    onSuccess: () => {
      // Invalidate the parent scenario to refetch with new scene
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * Hook to add a scene location (場面固有化 P1). key はシナリオ内一意の不変 ID。
 */
export function useCreateScenarioLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scenarioId, payload }: { scenarioId: string; payload: { key: string; name: string; description: string } }) =>
      api.createScenarioLocation(scenarioId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * Hook to rename a scene location's name/description (key is immutable).
 */
export function useRenameScenarioLocation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scenarioId, locationKey, payload }: { scenarioId: string; locationKey: string; payload: { name: string; description: string } }) =>
      api.renameScenarioLocation(scenarioId, locationKey, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/** レガシーシーンのテロップを話者付き行 (telop_lines) へ昇格する。 */
export function useInferSceneTelopLines() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ sceneId }: { sceneId: string }) => api.inferSceneTelopLines(sceneId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/** 登場人物宣言の追加 (docs/features/scenario-telop-speaker-plan.md §3.7)。 */
export function useCreateScenarioCharacter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, payload }: { scenarioId: string; payload: api.ScenarioCharacterPayload & { key: string } }) =>
      api.createScenarioCharacter(scenarioId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/** 登場人物の改名 (label / description / appears_on_screen)。 */
export function useRenameScenarioCharacter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, characterKey, payload }: { scenarioId: string; characterKey: string; payload: api.ScenarioCharacterPayload }) =>
      api.renameScenarioCharacter(scenarioId, characterKey, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/** 登場人物宣言の削除。 */
export function useDeleteScenarioCharacter() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, characterKey }: { scenarioId: string; characterKey: string }) =>
      api.deleteScenarioCharacter(scenarioId, characterKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * Hook to delete a scenario
 */
export function useDeleteScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.deleteScenario,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * Hook to delete a scene
 */
export function useDeleteScene() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: api.deleteScene,
    onSuccess: () => {
      // Invalidate the parent scenario to refetch with updated scene list
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * Hook to fetch scenario prompt options (tone, duration, etc.)
 */
export function useScenarioPromptOptions() {
  return useQuery({
    queryKey: ['scenario-prompt-options'],
    queryFn: api.getScenarioPromptOptions,
    staleTime: 1000 * 60 * 60, // Cache for 1 hour since this data rarely changes
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
  });
}

/**
 * シナリオ属性ツリー（映像スタイル等）の effective tree を取得する。
 */
export function useScenarioAttributes() {
  return useQuery({
    queryKey: ['scenario-attributes'],
    queryFn: api.getScenarioAttributes,
    staleTime: 1000 * 60 * 60,
    retry: 3,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 10000),
  });
}

/**
 * Hook to check brand logo status for a scenario
 */
export function useScenarioBrandLogoStatus(scenarioId: string | null) {
  return useQuery({
    queryKey: ['scenario-brand-logo', scenarioId],
    queryFn: () => api.checkScenarioBrandLogo(scenarioId!),
    enabled: !!scenarioId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to get product-brand linking info for a scenario
 */
export function useScenarioProductBrandInfo(scenarioId: string | null) {
  return useQuery({
    queryKey: ['scenario-product-brand-info', scenarioId],
    queryFn: () => api.getScenarioProductBrandInfo(scenarioId!),
    enabled: !!scenarioId,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to link a product to a brand
 */
export function useLinkProductToBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ brandId, productType, productId }: { brandId: string; productType: 'amazon' | 'manual'; productId: string }) =>
      api.linkProductToBrand(brandId, productType, productId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenario-product-brand-info'] });
    },
  });
}

/**
 * Hook to link a scenario to a brand
 */
export function useLinkScenarioBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, brandId }: { scenarioId: string; brandId: string }) =>
      api.linkScenarioBrand(scenarioId, brandId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenario-brand-logo'] });
    },
  });
}

/**
 * Hook to generate the next scene using AI
 */
export function useGenerateAiContinueScene() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, model }: { scenarioId: string; model?: string }) =>
      api.generateAiContinueScene(scenarioId, model),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * Hook to generate a product-focused last cut scene
 */
export function useGenerateProductLastCut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, model, force }: { scenarioId: string; model?: string; force?: boolean }) =>
      api.generateProductLastCut(scenarioId, model, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * Hook to generate a logo-only last cut scene
 */
export function useGenerateLogoLastCut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, model, force }: { scenarioId: string; model?: string; force?: boolean }) =>
      api.generateLogoLastCut(scenarioId, model, force),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * Hook to generate a logo-only title card (opening brand cut) scene
 */
export function useGenerateTitleCard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, model }: { scenarioId: string; model?: string }) =>
      api.generateTitleCard(scenarioId, model),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * Hook to attach a brand cut (title card / endcard) from an existing material (no AI, no charge)
 */
export function useAttachBrandCutFromMaterial() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, position, kind, imageUrl, materialId }: {
      scenarioId: string; position: 'start' | 'end'; kind: 'logo' | 'product'; imageUrl: string; materialId?: string;
    }) => api.attachBrandCutFromMaterial(scenarioId, { position, kind, imageUrl, materialId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * Hook to fetch deleted scenarios (trash)
 */
export function useDeletedScenarios(params?: { page?: number; limit?: number }) {
  return useQuery({
    queryKey: ['scenarios', 'trash', params?.page, params?.limit],
    queryFn: () => api.listDeletedScenarios(params),
  });
}

/**
 * Hook to restore a soft-deleted scenario
 */
export function useRestoreScenario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.restoreScenario,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios'] });
    },
  });
}

/**
 * Hook to permanently delete a soft-deleted scenario
 */
export function usePermanentlyDeleteScenario() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.permanentlyDeleteScenario,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios', 'trash'] });
    },
  });
}

/**
 * Hook to permanently delete multiple soft-deleted scenarios
 */
export function useBulkDeleteScenarios() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: api.bulkDeleteScenarios,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['scenarios', 'trash'] });
    },
  });
}

/**
 * Hook to fetch storyboard candidates for a scenario
 */
export function useStoryboards(scenarioId: string | null) {
  return useQuery({
    queryKey: ['storyboards', scenarioId],
    queryFn: () => api.listStoryboards(scenarioId!),
    enabled: !!scenarioId,
  });
}

/**
 * Hook to upload a user-provided image as a storyboard candidate
 */
export function useUploadStoryboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, file }: { scenarioId: string; file: File }) =>
      api.uploadStoryboard(scenarioId, file),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['storyboards', variables.scenarioId] });
      queryClient.invalidateQueries({ queryKey: ['scenarios', variables.scenarioId] });
    },
  });
}

/**
 * Hook to select a storyboard candidate (sets it as the video-generation reference)
 */
export function useSelectStoryboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, storyboardId }: { scenarioId: string; storyboardId: string }) =>
      api.selectStoryboard(scenarioId, storyboardId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['storyboards', variables.scenarioId] });
      queryClient.invalidateQueries({ queryKey: ['scenarios', variables.scenarioId] });
    },
  });
}

/**
 * Hook to delete a storyboard candidate
 */
export function useDeleteStoryboard() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ scenarioId, storyboardId }: { scenarioId: string; storyboardId: string }) =>
      api.deleteStoryboard(scenarioId, storyboardId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['storyboards', variables.scenarioId] });
      queryClient.invalidateQueries({ queryKey: ['scenarios', variables.scenarioId] });
    },
  });
}
